import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import MainApi from "../../../../../services/mainApi.service";
import { IDigitalCertificateFiles } from "../../../../../models/digital-certificate-file";
import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { DigitalCertificateFile } from "./DigitalCertificateFileForm/DigitalCertificateFile";
import { dateOnlyStringToNewDate } from "../../../../../utils/conversions";

const DIGITAL_CERTIFICATE_FILE_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/CompanyDigitalCertificateFile/";
const digitalCertificateFileService = new MainApi<IDigitalCertificateFiles>(
  DIGITAL_CERTIFICATE_FILE_ROUTE,
);

const translationPath =
  "page.register.digital_certificates.digitalCertificateFile.";

const DigitalCertificateFilePage = () => {
  const { id: companyDigitalCertificateId } = useParams();

  const { t } = useTranslation();

  const [digitalCertificateForm, setDigitalCertificateForm] = useState(false);

  const [selectedId, setSelectedId] = useState("");

  const columns: GridColDef<IDigitalCertificateFiles>[] = [
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Start Date",
      renderCell: ({ row: { initialDate } }) => {
        if (initialDate && initialDate.length > 0) {
          const date = dateOnlyStringToNewDate(initialDate);
          return (
            <OverflowTooltip
              text={date.toLocaleDateString("pt-BR")}
              tooltip={date.toLocaleDateString("pt-BR")}
              textTransform="none"
              variant="body2"
            />
          );
        }
        return "-";
      },
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End Date",
      renderCell: ({ row: { endDate } }) => {
        if (endDate && endDate.length > 0) {
          const date = dateOnlyStringToNewDate(endDate);
          return (
            <OverflowTooltip
              text={date.toLocaleDateString("pt-BR")}
              tooltip={date.toLocaleDateString("pt-BR")}
              textTransform="none"
              variant="body2"
            />
          );
        }
        return "-";
      },
    },
  ];

  const handleAdd = () => {
    setDigitalCertificateForm(true);
  };

  const handleRowClick = (params: GridRowParams<IDigitalCertificateFiles>) => {
    if (!params.row) return;
    setDigitalCertificateForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setDigitalCertificateForm(false);
    setSelectedId("");
  }, []);

  const params = useMemo(
    () => ({
      CompanyDigitalCertificateId: companyDigitalCertificateId,
    }),
    [],
  );

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {digitalCertificateForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <DigitalCertificateFile
            id={selectedId}
            onBackButton={handleBack}
            companyDigitalCertificateId={companyDigitalCertificateId}
          />
        </Box>
      )}
      {!digitalCertificateForm && (
        <Box sx={{ height: 600 }}>
          <Table<IDigitalCertificateFiles>
            showDefaultMacroFilters={false}
            service={digitalCertificateFileService}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "digital_certificate_files")}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputPlaceHolder={t(translationPath + "search")}
            addButtonLabel={
              t(translationPath + "add_digital_certificate_file") ||
              "Add Digital Certificate File"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DigitalCertificateFilePage;
